import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Users, Sparkles, FileDown } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { pollJob, downloadBlob } from '../../utils/aiJobs';
import { TeamPicker } from './TeamPicker';

const List = ({ title, items }) => (items?.length ? (
  <div>
    <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-slate-400">{title}</p>
    <ul className="list-disc space-y-1 pl-5 text-sm text-slate-200">{items.map((x, i) => <li key={i}>{x}</li>)}</ul>
  </div>
) : null);

const Opinion = ({ o }) => (
  <div data-testid={`team-opinion-${o.area}`} className="space-y-3 rounded-xl border border-slate-800 bg-slate-950/50 p-4">
    <p className="font-semibold text-indigo-200">Socio de {o.area_name}</p>
    <p className="text-sm text-slate-300">{o.diagnostico}</p>
    <List title="Riesgos" items={o.riesgos} />
    <List title="Acciones" items={o.acciones} />
    <List title="Normas" items={o.normas} />
  </div>
);

export const TeamReviewPanel = ({ matterId, country, areas = [], onBlocked, onDone }) => {
  const { api } = useAuth();
  const [review, setReview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [team, setTeam] = useState([]);
  const [motivo, setMotivo] = useState(null);
  const [suggested, setSuggested] = useState([]);
  const [suggesting, setSuggesting] = useState(false);

  const suggest = async () => {
    setSuggesting(true);
    try {
      const { data } = await api.post('/specialists/team-suggest', { matter_id: matterId, country });
      setTeam(data.areas);
      setSuggested(data.areas);
      setMotivo(data.motivo);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Cerebro no pudo sugerir el equipo');
    } finally {
      setSuggesting(false);
    }
  };

  useEffect(() => {
    api.get('/specialists/team-reviews', { params: { matter_id: matterId } }).then((r) => setReview(r.data.reviews[0] || null)).catch(() => {});
  }, [api, matterId]);

  const run = async () => {
    setBusy(true);
    try {
      if (team.length < 2) { toast.error('Elige al menos 2 socios'); return; }
      const { data } = await api.post('/specialists/team-review', { matter_id: matterId, country, areas: team, motivo: team.join() === suggested.join() ? motivo : `Equipo ajustado por el usuario${motivo ? ' a partir de la sugerencia de Cerebro' : ''}.` });
      setReview(await pollJob(api, data.job_id));
      onDone?.();
    } catch (err) {
      const d = err.response?.data?.detail;
      if (d?.code) onBlocked?.(d.code); else toast.error(d || err.message || 'El equipo no pudo revisar el caso');
    } finally {
      setBusy(false);
    }
  };

  const pdf = async () => {
    try {
      const r = await api.get(`/specialists/team-reviews/${review.id}/pdf`, { responseType: 'blob' });
      downloadBlob(r.data, 'estrategia_equipo_socios.pdf');
    } catch { toast.error('No se pudo generar el PDF'); }
  };

  const j = review?.joint;
  return (
    <div className="space-y-5" data-testid="team-review-panel">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-800 pb-3">
        <div>
          <p className="flex items-center gap-2 font-semibold text-white"><Users className="h-4 w-4 text-indigo-300" /> Equipo de Socios</p>
          <p className="text-xs text-slate-400">Cerebro sugiere 2 especialistas; puedes cambiarlos o sumar un tercero antes de revisar.</p>
        </div>
        <button data-testid="team-review-run-btn" onClick={run} disabled={busy || team.length < 2} className="inline-flex items-center gap-2 rounded-full bg-indigo-500 px-5 py-2 text-sm font-semibold text-white hover:bg-indigo-400 transition-colors disabled:opacity-60">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} {busy ? 'Los socios están revisando (1-2 min)…' : review ? 'Nueva revisión' : 'Revisar caso en equipo'}
        </button>
      </div>
      <TeamPicker areas={areas} team={team} setTeam={setTeam} motivo={motivo} suggesting={suggesting} onSuggest={suggest} />
      {!review && !busy && <p className="text-sm text-slate-400" data-testid="team-review-empty">Aún no hay una revisión en equipo para este caso.</p>}
      {review && (
        <div className="space-y-5" data-testid="team-review-result">
          <button data-testid="team-review-pdf-btn" onClick={pdf} className="inline-flex items-center gap-2 rounded-full border border-amber-400/60 px-4 py-2 text-sm font-semibold text-amber-200 hover:bg-amber-500/10 transition-colors">
            <FileDown className="h-4 w-4" /> Descargar PDF para el cliente
          </button>
          <p className="text-sm text-slate-300"><span className="font-semibold text-amber-300">Equipo:</span> {review.team.map((t) => t.area_name).join(' + ')}. {review.motivo}</p>
          <div className="rounded-2xl border border-indigo-500/40 bg-indigo-500/10 p-5 space-y-4" data-testid="team-joint-strategy">
            <p className="text-xs font-semibold uppercase tracking-wider text-indigo-300">Estrategia conjunta</p>
            <p className="text-white">{j?.resumen}</p>
            <ol className="space-y-2 text-sm">
              {(j?.plan || []).map((p, i) => (
                <li key={i} className="rounded-lg bg-slate-950/50 p-3"><span className="font-semibold text-white">{i + 1}. {p.paso}</span> <span className="text-xs text-indigo-300">· {p.responsable}</span><p className="text-slate-300">{p.detalle}</p></li>
              ))}
            </ol>
            <List title="Sinergias" items={j?.sinergias} />
            <List title="Tensiones y cómo resolverlas" items={j?.conflictos} />
            <List title="Próximos 7 días" items={j?.proximos_7_dias} />
            {j?.vigencia && <p data-testid="team-vigencia" className="text-xs text-slate-400">{j.vigencia}</p>}
          </div>
          <div className="grid gap-4 md:grid-cols-2">{review.team.map((o) => <Opinion key={o.area} o={o} />)}</div>
        </div>
      )}
    </div>
  );
};
